import { MouseEvent, useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Routes } from "router";
import { Typography } from "theme";
import { ArrowDown, ArrowRight, Check, List, Logout, Minus, Plus, StarActive, Tiles, User } from "theme/icons";

import { Box, Fade, Grid2, Popper } from "@mui/material";
import { styled } from "@mui/system";

import { useAuth } from "../../../utils/context/Auth/AuthContext.tsx";

import CreatePromocodeModal from "./CreatePromocodeModal.tsx";

const StyledImage = styled("img")(() => ({
  borderRadius: "50%",
  width: "42px",
  height: "42px",
  objectFit: "cover",
}));

const StyledButton = styled("button")(({ theme }) => ({
  background: "none",
  border: "none",
  cursor: "pointer",
  transition: "0.3s",
  display: "flex",
  alignItems: "center",
  columnGap: theme.spacing(1),

  "&:hover": {
    backgroundColor: theme.palette.secondary.contrastText,
  },
}));

const StyledLogoutButton = styled("button")(({ theme }) => ({
  padding: theme.spacing(0.75, 1.5),
  background: "none",
  border: "none",
  cursor: "pointer",
  transition: "0.3s",
  display: "flex",
  alignItems: "center",
  columnGap: theme.spacing(1),
  width: '100%',

  "&:hover": {
    backgroundColor: theme.palette.secondary.contrastText,
  },
}));

const PopoverContent = styled(Box)(({ theme }) => ({
  borderWidth: 1,
  borderStyle: 'dashed',
  borderColor: theme.palette.secondary.main,
  backgroundColor: theme.palette.primary.main,
  width: "240px",
}))

const IconWrapper = styled(Box)(({ theme }) => ({
  borderRadius: "50%",
  backgroundColor: theme.palette.secondary.contrastText,
  padding: theme.spacing(0.5),
  display: "inline-flex",
  justifyContent: "center",
}))

const UserInfo = () => {
  const { user, clearToken } = useAuth();
  const navigate = useNavigate();

  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [promocodeModalOpen, setPromocodeModalOpen] = useState(false);

  const handleClick = useCallback((event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    setOpen((previousOpen) => !previousOpen);
  }, []);

  const canBeOpen = open && Boolean(anchorEl);
  const id = canBeOpen ? "transition-popper" : undefined;

  const onLogout = useCallback(() => {
    clearToken();
    navigate(Routes.SignIn);
  }, [clearToken, navigate])

  const handleProtectedNavigation = useCallback((route: string) => {
    const password = prompt("Введіть пароль для доступу:");
    if (password === "1234") {
      navigate(route);
    } else if (password !== null) {
      alert("Невірний пароль");
    }
  }, [navigate]);

  return (
    <Grid2 container justifyContent="space-between">
      <Box>
        <StyledButton aria-describedby={id} onClick={handleClick}>
          <StyledImage alt="User avatar" src={user?.avatarUrl} />
          <Box component="span">
            <Typography variant="body1" weight="semiBold">
              {user?.email}
            </Typography>
          </Box>
          <Box component="span" width="14px">
            {open ? <ArrowDown /> : <ArrowRight />}
          </Box>
        </StyledButton>
        <Popper id={id} open={open} anchorEl={anchorEl} transition>
          {({ TransitionProps }) => (
            <Fade {...TransitionProps} timeout={350}>
              <PopoverContent>
                <StyledLogoutButton onClick={() => navigate(Routes.Clients)}>
                  <IconWrapper>
                    <User />
                  </IconWrapper>
                  <Typography variant="body2">Клієнти</Typography>
                </StyledLogoutButton>
                <Box mt={1}>
                  <StyledLogoutButton onClick={() => { setOpen(false); setPromocodeModalOpen(true); }}>
                    <IconWrapper>
                      <Plus />
                    </IconWrapper>
                    <Typography variant="body2">Створити промокод</Typography>
                  </StyledLogoutButton>
                </Box>
                <Box mt={1}>
                  <StyledLogoutButton onClick={() => navigate(Routes.CheckIn)}>
                    <IconWrapper>
                      <Check />
                    </IconWrapper>
                    <Typography variant="body2">Відмітитись</Typography>
                  </StyledLogoutButton>
                </Box>
                <Box mt={1}>
                  <StyledLogoutButton onClick={() => navigate(Routes.Spending)}>
                    <IconWrapper>
                      <Minus />
                    </IconWrapper>
                    <Typography variant="body2">Витрати</Typography>
                  </StyledLogoutButton>
                </Box>
                {import.meta.env.VITE_ADMIN_EMAIL === user?.email && (
                  <>
                    <Box mt={1}>
                      <StyledLogoutButton onClick={() => handleProtectedNavigation(Routes.Statistics)}>
                        <IconWrapper>
                          <StarActive />
                        </IconWrapper>
                        <Typography variant="body2">Статистика</Typography>
                      </StyledLogoutButton>
                    </Box>
                    <Box mt={1}>
                      <StyledLogoutButton onClick={() => handleProtectedNavigation(Routes.Reports)}>
                        <IconWrapper>
                          <List />
                        </IconWrapper>
                        <Typography variant="body2">Звіти</Typography>
                      </StyledLogoutButton>
                    </Box>
                    <Box mt={1}>
                      <StyledLogoutButton onClick={() => handleProtectedNavigation(Routes.Bonuses)}>
                        <IconWrapper>
                          <Tiles />
                        </IconWrapper>
                        <Typography variant="body2">Бонуси</Typography>
                      </StyledLogoutButton>
                    </Box>
                  </>
                )}
                <Box mt={1}>
                  <StyledLogoutButton onClick={onLogout}>
                    <IconWrapper>
                      <Logout />
                    </IconWrapper>
                    <Typography variant="body2">Вийти з облікового запису</Typography>
                  </StyledLogoutButton>
                </Box>
              </PopoverContent>
            </Fade>
          )}
        </Popper>
      </Box>
      <CreatePromocodeModal open={promocodeModalOpen} onClose={() => setPromocodeModalOpen(false)} />
    </Grid2>
  );
};

export default UserInfo;
